// The long sounds: one bed at a time, swapped rather than stacked.
//
// Everything else in here is an *event* — a step, a forge, an ingot landing —
// and the engine's whole job is deciding which of them to drop. A bed is the
// opposite shape: exactly one is sounding, it loops until something else takes
// its place, and the interesting question is not whether to play it but how to
// stop the one already going. So it is a small state machine rather than a
// second path through the engine.
//
// Three things it has to survive, and all three are ordinary:
//
//  - **Asked for before the first gesture.** The menu mounts and asks for menu
//    music while the browser still refuses to make a sound. The mood is
//    remembered and started when `unlock` arrives.
//  - **Asked for twice.** React mounts, unmounts and mounts again in
//    StrictMode, and a shell remounts for reasons that have nothing to do with
//    music. Requesting what is already playing is a no-op, so a remount is
//    free and cannot restart the track.
//  - **Asked for with no music to play.** The registries below are empty on
//    purpose (§1.2) — there are no tracks yet. Nothing is played, nothing is
//    fetched and nothing is said, and the day a file is added the wiring
//    already in `ModeMenu` and `GameShell` starts working with no code change.

import type { AudioPort, VoiceHandle } from "./port";
import { type Bus, soundUrl } from "./sounds";

/**
 * What the game is doing, as far as a bed is concerned.
 *
 * Deliberately coarse. `ModeMenu` is outside the game store and `GameShell` is
 * inside it — `App` renders one or the other — so these two genuinely alternate
 * and a mood change is always a mount.
 */
export type Mood = "menu" | "game";

export interface Track {
  /** Under `public/sounds/`, like everything else the port loads. */
  file: string;
  /**
   * How loud this track is against the others in its list.
   *
   * The *bus* is the player's business and is already set by `applyPrefs`; this
   * is only what keeps two tracks in one mood from being different volumes.
   */
  gain: number;
}

/** The tracks a mood may draw from. A mood with none is silent, not an error. */
export type MusicRegistry = Partial<Record<Mood, readonly Track[]>>;

/**
 * The music, and there is none yet (§1.2).
 *
 * Empty on purpose rather than absent: the type is the gate, so adding a row
 * here is the whole of adding music. Exported because `sound` wires it and
 * because the test injects its own.
 */
export const TRACKS: MusicRegistry = {};

/**
 * The ambience, keyed by the same moods and played on its own bus.
 *
 * A second registry rather than a second class — what differs between a bed of
 * music and a bed of room tone is which slider silences it, which is the same
 * reading `playUi` takes of the `ui` bus.
 */
export const AMBIENCE: MusicRegistry = {};

/**
 * How long one bed takes to become another.
 *
 * Two seconds: long enough that neither track is heard to start or stop, short
 * enough that a player who has just clicked into a game is not listening to the
 * menu under it. Beds are the one place in this system a fade is *heard* rather
 * than merely used to stop a click, so it is measured in seconds where every
 * other ramp here is measured in milliseconds.
 */
export const CROSSFADE_SEC = 2;

/** Nothing is playing; the incoming voice ramps up from here. */
const SILENT = 0;

/**
 * One bed at a time, on one bus.
 *
 * The port is taken as a thunk because the engine's is built on first use — a
 * player constructed against the port itself would either force it into
 * existence at import time or hold a different one from the engine's, and the
 * second failure is invisible: two graphs, one of them never unlocked.
 */
export class MusicPlayer {
  /** The mood of the voice that is sounding. Dedupe is against this. */
  private playing: Mood | null = null;
  /** The mood of a load in flight, so a remount mid-fetch does not fetch twice. */
  private pending: Mood | null = null;
  /** The last mood asked for, replayed by `onUnlocked`. */
  private wanted: Mood | null = null;
  private voice: VoiceHandle | null = null;
  /** Bumped by every request, so a load that resolves late is discarded. */
  private token = 0;
  /** Urls that would not load. Never asked for twice — the engine's rule. */
  private failed = new Set<string>();

  constructor(
    private readonly port: () => AudioPort,
    private readonly bus: Bus,
    private readonly tracks: MusicRegistry
  ) {}

  /**
   * Play this mood's bed, if it is not already the one playing.
   *
   * Silent and remembered in the two cases that are not failures: before the
   * first gesture, and with nothing registered for the mood.
   */
  request(mood: Mood): void {
    this.wanted = mood;
    if (this.playing === mood || this.pending === mood) return;
    this.start(mood);
  }

  /** Fade the bed out and forget the mood, so nothing resumes it. */
  stop(fadeSec = CROSSFADE_SEC): void {
    this.wanted = null;
    this.pending = null;
    this.playing = null;
    // Anything still loading now belongs to a mood nobody wants.
    this.token++;
    const voice = this.voice;
    this.voice = null;
    if (voice) this.fadeOut(voice, fadeSec);
  }

  /**
   * The context has opened.
   *
   * Internal to `src/client/ai/audio/` — called by `SoundEngine.unlock`, which is the one
   * thing that knows when the browser has let go. Nothing outside this
   * directory should call it, and nothing does.
   */
  onUnlocked(): void {
    if (this.wanted) this.request(this.wanted);
  }

  private start(mood: Mood): void {
    const port = this.port();
    // Before the first gesture there is nothing to play into. The mood is kept
    // and `onUnlocked` replays it.
    if (!port.unlocked) return;

    const list = this.tracks[mood];
    // No music for this mood. Not an error and not worth saying: a game with
    // no soundtrack is a game.
    if (!list || list.length === 0) return;

    // Legal here: this is presentation, it reaches no rule, and no replay has
    // to reproduce which of a mood's tracks a session happened to play.
    const track = list[Math.floor(Math.random() * list.length)];
    const url = soundUrl(track.file);
    // A file that failed once is never asked for again. A bed is requested on
    // every mount, so a retry here is a fetch per screen for the whole session.
    if (this.failed.has(url)) return;

    const token = ++this.token;
    this.pending = mood;
    void port.load(url).then((buffer) => {
      // A newer request won while this was in flight; it owns `pending` now.
      if (token !== this.token) return;
      this.pending = null;
      if (buffer == null) {
        this.failed.add(url);
        return;
      }
      this.swap(port, buffer, track, mood);
    });
  }

  private swap(port: AudioPort, buffer: unknown, track: Track, mood: Mood): void {
    const outgoing = this.voice;
    if (outgoing) this.fadeOut(outgoing, CROSSFADE_SEC);

    // Started at nothing and ramped up, so the two beds cross rather than the
    // new one landing on top of the old one at full.
    const voice = port.play(buffer, {
      bus: this.bus,
      gain: SILENT,
      pan: 0,
      rate: 1,
      loop: true
    });
    voice.setGain(track.gain, CROSSFADE_SEC);
    this.voice = voice;
    this.playing = mood;
  }

  /**
   * Ramp a bed away and end it.
   *
   * Both calls, deliberately: `setGain` is the fade that is *heard*, and `stop`
   * is what frees the source — handed the same length so a port whose stop cuts
   * hard cannot clip the fade short.
   */
  private fadeOut(voice: VoiceHandle, fadeSec: number): void {
    voice.setGain(SILENT, fadeSec);
    voice.stop(fadeSec);
  }
}
